import { CheckboxEditor, DateTimeEditor, DropdownEditor, Field, TextAreaEditor, TextEditor } from '@approvalmax/ui';
import { Grid } from '@approvalmax/ui/src/components';
import { useAtomValue } from 'jotai';
import { constants } from 'modules/common';
import { domain } from 'modules/data';
import { PlainDataProvider } from 'modules/data-providers';
import { useSelector } from 'modules/react-redux';
import { SectionContainer } from 'pages/requestForm/components/SectionContainer';
import { FC, useCallback } from 'react';
import { useNetSuiteJournalEntryFieldSettings } from 'shared/hooks';

import { getNetSuiteContext } from '../../../../../selectors/netsuite/netSuiteSelectors';
import { NetSuiteCurrencySection, NetSuiteCustomFields } from '../../../components';
import { useNetSuiteJournalEntryLines } from '../../../hooks';
import {
    isFieldEditable,
    isFieldMandatory,
    isFieldReadOnly,
    isFieldVisible,
    isOptionalFieldInvalid,
} from '../../../NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { netSuiteShowErrorsState } from '../../../resources/states';
import { useNetSuiteJournalEntryFields } from '../../NetSuiteJournalEntryRequestForm.hooks';
import { netSuiteJournalEntryFieldValuesState } from '../../NetSuiteJournalEntryRequestForm.states';
import { NetSuiteJournalEntryFields } from '../../NetSuiteJournalEntryRequestForm.types';
import { messages } from './FieldsSection.messages';
import { FieldsSectionProps } from './FieldsSection.types';

const { netSuiteConstants } = constants;

export const FieldsSection: FC<FieldsSectionProps> = (props) => {
    const { workflowId } = props;

    const netSuiteEditingContext = useSelector(getNetSuiteContext);
    const showErrors = useAtomValue(netSuiteShowErrorsState);
    const fieldValues = useAtomValue(netSuiteJournalEntryFieldValuesState);

    const { currencies, documentFields } = netSuiteEditingContext;

    const { onFieldChange, updateFieldsBulk } = useNetSuiteJournalEntryFields();

    const { updateItemListOnCurrencyChange } = useNetSuiteJournalEntryLines();

    const fieldSettings = useNetSuiteJournalEntryFieldSettings(documentFields);

    const onEditorChange = useCallback(
        <K extends keyof NetSuiteJournalEntryFields>(key: K) =>
            (value: NetSuiteJournalEntryFields[K]) =>
                onFieldChange(key, value),
        [onFieldChange]
    );

    const onCurrencyChange = useCallback(
        (currency: domain.NetSuiteCurrency | null) => {
            updateItemListOnCurrencyChange(currency);

            updateFieldsBulk({
                currency,
                exchangeRate: null,
            });
        },
        [updateFieldsBulk, updateItemListOnCurrencyChange]
    );

    return (
        <>
            <SectionContainer title={messages.journalEntryDetailsSectionTitle}>
                <Grid gridTemplateColumns='repeat(auto-fill, 267px)' rowGap={24} columnGap={32} padding='0 0 24'>
                    {isFieldVisible(fieldSettings.reference) && (
                        <Field title={messages.transactionReferenceFieldText}>
                            <TextEditor
                                value={fieldValues.reference || ''}
                                placeholder={
                                    isFieldEditable(fieldSettings.reference) ? messages.textFieldPlaceholder : ''
                                }
                                maxLength={netSuiteConstants.referenceMaxLength}
                                invalid={isOptionalFieldInvalid(
                                    showErrors,
                                    fieldValues.reference,
                                    fieldSettings.reference
                                )}
                                disabled={isFieldReadOnly(fieldSettings.reference)}
                                onChange={onEditorChange('reference')}
                                changeOnBlur
                            />
                        </Field>
                    )}

                    <Field title={messages.transactionDateFieldText} required>
                        <DateTimeEditor
                            value={fieldValues.transactionDate}
                            placeholder={messages.dateFieldPlaceholder}
                            invalid={showErrors && !fieldValues.transactionDate}
                            onChange={onEditorChange('transactionDate')}
                        />
                    </Field>

                    {isFieldVisible(fieldSettings.reversalDate) && (
                        <Field
                            title={messages.reversalDateFieldText}
                            required={isFieldMandatory(fieldSettings.reversalDate)}
                        >
                            <DateTimeEditor
                                value={fieldValues.reversalDate}
                                placeholder={messages.dateFieldPlaceholder}
                                onChange={onEditorChange('reversalDate')}
                                invalid={isOptionalFieldInvalid(
                                    showErrors,
                                    fieldValues.reversalDate,
                                    fieldSettings.reversalDate
                                )}
                                disabled={isFieldReadOnly(fieldSettings.reversalDate)}
                            />
                        </Field>
                    )}

                    {isFieldVisible(fieldSettings.reversalDefer) && (
                        <Field title={messages.reversalDeferFieldText}>
                            <CheckboxEditor
                                onChange={onEditorChange('reversalDefer')}
                                value={fieldValues.reversalDefer || false}
                                disabled={isFieldReadOnly(fieldSettings.reversalDefer)}
                            >
                                {messages.reversalDeferFieldLabel}
                            </CheckboxEditor>
                        </Field>
                    )}

                    {isFieldVisible(fieldSettings.class) && (
                        <Field title={messages.classFieldText} required={isFieldMandatory(fieldSettings.class)}>
                            <PlainDataProvider items={netSuiteEditingContext.classes}>
                                <DropdownEditor
                                    value={fieldValues.class}
                                    singleLinePerItem={false}
                                    onChange={onEditorChange('class')}
                                    placeholder={
                                        isFieldEditable(fieldSettings.class) ? messages.classFieldPlaceholder : ''
                                    }
                                    invalid={isOptionalFieldInvalid(showErrors, fieldValues.class, fieldSettings.class)}
                                    disabled={isFieldReadOnly(fieldSettings.class)}
                                />
                            </PlainDataProvider>
                        </Field>
                    )}

                    {isFieldVisible(fieldSettings.department) && (
                        <Field
                            title={messages.departmentFieldText}
                            required={isFieldMandatory(fieldSettings.department)}
                        >
                            <PlainDataProvider items={netSuiteEditingContext.departments}>
                                <DropdownEditor
                                    value={fieldValues.department}
                                    singleLinePerItem={false}
                                    onChange={onEditorChange('department')}
                                    placeholder={
                                        isFieldEditable(fieldSettings.department)
                                            ? messages.departmentFieldPlaceholder
                                            : ''
                                    }
                                    invalid={isOptionalFieldInvalid(
                                        showErrors,
                                        fieldValues.department,
                                        fieldSettings.department
                                    )}
                                    disabled={isFieldReadOnly(fieldSettings.department)}
                                />
                            </PlainDataProvider>
                        </Field>
                    )}

                    {isFieldVisible(fieldSettings.location) && (
                        <Field title={messages.locationFieldText} required={isFieldMandatory(fieldSettings.location)}>
                            <PlainDataProvider items={netSuiteEditingContext.locations}>
                                <DropdownEditor
                                    value={fieldValues.location}
                                    singleLinePerItem={false}
                                    onChange={onEditorChange('location')}
                                    placeholder={
                                        isFieldEditable(fieldSettings.location) ? messages.locationFieldPlaceholder : ''
                                    }
                                    invalid={isOptionalFieldInvalid(
                                        showErrors,
                                        fieldValues.location,
                                        fieldSettings.location
                                    )}
                                    disabled={isFieldReadOnly(fieldSettings.location)}
                                />
                            </PlainDataProvider>
                        </Field>
                    )}

                    <NetSuiteCustomFields workflowId={workflowId} showErrors={showErrors} noFieldMargins />
                </Grid>
            </SectionContainer>

            <NetSuiteCurrencySection
                currencies={currencies}
                fieldValues={fieldValues}
                fieldSettings={fieldSettings}
                onExchangeRateChange={onEditorChange('exchangeRate')}
                onCurrencyChange={onCurrencyChange}
            />

            {isFieldVisible(fieldSettings.memo) && (
                <SectionContainer title={messages.memoSectionTitle}>
                    <Grid gridTemplateColumns='565px'>
                        <TextAreaEditor
                            value={fieldValues.memo || ''}
                            onChange={onEditorChange('memo')}
                            placeholder={isFieldEditable(fieldSettings.memo) ? messages.textFieldPlaceholder : ''}
                            changeOnBlur
                            minHeight={5}
                            maxLength={netSuiteConstants.memoMaxLength}
                            invalid={isOptionalFieldInvalid(showErrors, fieldValues.memo, fieldSettings.memo)}
                            disabled={isFieldReadOnly(fieldSettings.memo)}
                        />
                    </Grid>
                </SectionContainer>
            )}
        </>
    );
};

FieldsSection.displayName = 'FieldsSection';
